import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, ExternalLink, Send } from "lucide-react";

const STATUSES = ["all", "paid", "forwarded", "fulfilled", "shipped", "delivered", "cancelled", "refunded"];

const statusBadge = (s) => {
  const palette = {
    paid: "var(--rs-oxblood)",
    forwarded: "var(--rs-terracotta)",
    fulfilled: "#7a5b3a",
    shipped: "#4a6b8a",
    delivered: "#3a5a3a",
    cancelled: "#444",
    refunded: "#7a4a4a",
  };
  return palette[s] || "var(--rs-oxblood)";
};

const STATUS_HINT = {
  paid: "Awaiting forward — place this on supplier",
  forwarded: "Supplier has the order",
  fulfilled: "Supplier confirmed fulfilment",
  shipped: "On the way to the customer",
  delivered: "Customer has received",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [forwarding, setForwarding] = useState(false);

  const load = () => {
    const params = status !== "all" ? { params: { status } } : {};
    api.get("/admin/orders", params).then((r) => setOrders(r.data || []));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const forwardNow = async () => {
    setForwarding(true);
    try {
      const { data } = await api.post(`/admin/orders/${editor.order_id}/forward`);
      if (data.all_submitted) {
        toast.success("All supplier groups auto-submitted — order forwarded");
      } else {
        toast("Dispatch sheet emailed — manual placement required", {
          description: "Supplier APIs aren't wired yet. Place the order via the portal links, then set status to forwarded.",
        });
      }
      setEditor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Forward failed");
    } finally {
      setForwarding(false);
    }
  };

  const forwardCell = (o) => {
    if (o.status !== "paid") return null;
    if (o.forward_status === "action_required") {
      return (
        <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: "#c08a2d" }}>
          action required
        </div>
      );
    }
    if (o.forward_at) {
      const ms = new Date(o.forward_at) - Date.now();
      const label = ms <= 0 ? "due now" : ms < 3600e3 ? `in ${Math.ceil(ms / 60e3)}m` : `in ${Math.round(ms / 3600e3)}h`;
      return (
        <div className="text-[10px] uppercase tracking-widest text-muted-bone mt-1">
          auto-forward {label}
        </div>
      );
    }
    return null;
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${editor.order_id}`, {
        status: editor.status,
        tracking_number: editor.tracking_number || null,
        carrier: editor.carrier || null,
        notes: editor.admin_notes || null,
        supplier_order_ref: editor.supplier_order_ref || null,
      });
      toast.success(`Order ${editor.order_id.slice(-8)} updated`);
      setEditor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const totalSupplierCost = editor
    ? editor.items.reduce((s, it) => s + (Number(it.supplier_cost) || 0) * (it.quantity || 1), 0)
    : 0;
  const margin = editor ? editor.amount - totalSupplierCost : 0;

  return (
    <div data-testid="admin-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <div>
          <div className="overline mb-3">Fulfilment · Drop-ship</div>
          <h1 className="font-serif-display text-5xl text-bone">Orders</h1>
          <p className="text-muted-bone mt-2 text-sm">{orders.length} order{orders.length !== 1 ? "s" : ""} {status !== "all" && `· filtered: ${status}`}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`admin-orders-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.22em] border ${status === s ? "text-bone" : "text-muted-bone"}`}
              style={{ borderColor: status === s ? "var(--rs-terracotta)" : "var(--rs-border)" }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="border" style={{ borderColor: "var(--rs-border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-[0.22em] text-muted-bone">
              <th className="text-left px-6 py-4">Order</th>
              <th className="text-left px-6 py-4">Customer</th>
              <th className="text-left px-6 py-4">Items</th>
              <th className="text-left px-6 py-4">Amount</th>
              <th className="text-left px-6 py-4">Status</th>
              <th className="text-left px-6 py-4">Supplier ref</th>
              <th className="text-right px-6 py-4">Manage</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr
                key={o.order_id}
                data-testid={`admin-order-row-${o.order_id}`}
                className="border-t"
                style={{ borderColor: "var(--rs-border)" }}
              >
                <td className="px-6 py-4 text-bone font-mono text-xs">{o.order_id.slice(-10)}</td>
                <td className="px-6 py-4 text-muted-bone">{o.user_email || "guest"}</td>
                <td className="px-6 py-4 text-muted-bone">{o.items.length}</td>
                <td className="px-6 py-4 text-bone">${o.amount.toFixed(2)}</td>
                <td className="px-6 py-4">
                  <span
                    className="px-2 py-1 text-[10px] uppercase tracking-widest text-bone"
                    style={{ background: statusBadge(o.status) }}
                  >
                    {o.status}
                  </span>
                  {forwardCell(o)}
                </td>
                <td className="px-6 py-4 text-muted-bone text-xs font-mono">{o.supplier_order_ref || "—"}</td>
                <td className="px-6 py-4 text-right">
                  <button
                    data-testid={`admin-order-edit-${o.order_id}`}
                    onClick={() => setEditor({ ...o, admin_notes: o.admin_notes || "", supplier_order_ref: o.supplier_order_ref || "" })}
                    className="text-xs uppercase tracking-[0.22em] text-muted-bone hover:text-bone"
                  >
                    Manage →
                  </button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-10 text-center text-muted-bone">
                  No orders {status !== "all" ? `with status "${status}"` : "yet"}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editor && (
        <div
          data-testid="admin-order-editor"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(10,10,10,0.92)" }}
        >
          <div
            className="w-full max-w-3xl border max-h-[90vh] overflow-y-auto"
            style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }}
          >
            <div
              className="flex items-center justify-between px-6 py-5 border-b"
              style={{ borderColor: "var(--rs-border)" }}
            >
              <div>
                <div className="overline">Order · {editor.order_id.slice(-10)}</div>
                <div className="font-serif-display text-2xl text-bone">
                  ${editor.amount.toFixed(2)} {editor.currency.toUpperCase()}
                </div>
                <div className="text-muted-bone text-xs mt-1">{editor.user_email || "guest"}</div>
              </div>
              <button onClick={() => setEditor(null)} className="text-muted-bone hover:text-bone">
                <X strokeWidth={1.5} size={18} />
              </button>
            </div>

            {/* Shipping address */}
            <div className="p-6 border-b" style={{ borderColor: "var(--rs-border)" }}>
              <div className="overline mb-3">Ship to</div>
              {editor.shipping?.line1 ? (
                <div data-testid="admin-order-shipping" className="text-sm text-bone leading-relaxed">
                  {editor.shipping.name || editor.customer_name || editor.user_email}<br />
                  {editor.shipping.line1}{editor.shipping.line2 ? `, ${editor.shipping.line2}` : ""}<br />
                  {editor.shipping.city} {editor.shipping.state} {editor.shipping.postal_code}<br />
                  {editor.shipping.country}
                </div>
              ) : (
                <p data-testid="admin-order-no-shipping" className="text-xs text-muted-bone">
                  No shipping address stored (older order) — check the Stripe dashboard for session {editor.session_id?.slice(-10)}.
                </p>
              )}
            </div>

            {/* Supplier dispatch list */}
            <div className="p-6 border-b space-y-4" style={{ borderColor: "var(--rs-border)" }}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="overline">Forward to supplier — items</div>
                {editor.status === "paid" && (
                  <button
                    data-testid="admin-order-forward-now-btn"
                    type="button"
                    onClick={forwardNow}
                    disabled={forwarding}
                    className="px-5 py-2 text-xs uppercase tracking-[0.22em] text-bone disabled:opacity-50 flex items-center gap-2"
                    style={{ background: "var(--rs-oxblood)" }}
                  >
                    <Send strokeWidth={1.5} size={13} />
                    {forwarding ? "Forwarding…" : "Forward now"}
                  </button>
                )}
              </div>
              {editor.status === "paid" && editor.forward_at && !editor.forward_processed_at && (
                <p className="text-xs" style={{ color: "rgba(212,175,55,0.8)" }}>
                  Scheduled to auto-forward {new Date(editor.forward_at).toLocaleString()} (2-day delay — adjustable in Vendors).
                </p>
              )}
              {editor.supplier_forwards?.length > 0 && (
                <div className="space-y-2" data-testid="admin-order-forward-summary">
                  {editor.supplier_forwards.map((g, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border px-3 py-2" style={{ borderColor: "var(--rs-border)" }}>
                      <span className="text-bone">{g.supplier_name} · {g.items.length} item(s)</span>
                      <span
                        className="px-2 py-1 text-[10px] uppercase tracking-widest text-bone"
                        style={{ background: g.status === "submitted" ? "#2f6f4f" : "#8a6d1a" }}
                      >
                        {g.status === "submitted" ? `submitted ${g.supplier_order_ref || ""}` : "manual required"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-bone">
                Open each supplier link, place the order with the customer's shipping address above, and paste the supplier order reference below.
              </p>
              <div className="space-y-3">
                {editor.items.map((it, i) => (
                  <div
                    key={i}
                    data-testid={`admin-order-item-${i}`}
                    className="border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    style={{ borderColor: "var(--rs-border)" }}
                  >
                    <div className="flex items-center gap-3">
                      {it.image && <img src={it.image.startsWith("/api/files/") ? `${process.env.REACT_APP_BACKEND_URL}${it.image}` : it.image} alt={it.name} className="w-12 h-16 object-cover" />}
                      <div>
                        <div className="text-bone text-sm">{it.name} × {it.quantity}</div>
                        <div className="text-xs text-muted-bone">
                          {it.supplier_name || "No supplier set"}
                          {it.supplier_sku ? ` · SKU ${it.supplier_sku}` : ""}
                          {it.supplier_cost ? ` · Cost $${Number(it.supplier_cost).toFixed(2)}` : ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-muted-bone text-xs">${(it.price * it.quantity).toFixed(2)}</span>
                      {it.supplier_url ? (
                        <a
                          data-testid={`admin-order-supplier-link-${i}`}
                          href={it.supplier_url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-2 text-xs uppercase tracking-[0.22em] text-bone border flex items-center gap-1 hover:bg-white/5"
                          style={{ borderColor: "rgba(245,242,235,0.25)" }}
                        >
                          Open <ExternalLink strokeWidth={1.5} size={12} />
                        </a>
                      ) : (
                        <span className="text-xs text-muted-bone italic">No URL set</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-sm pt-2">
                <span className="overline">Revenue</span>
                <span className="text-bone">${editor.amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="overline">Supplier cost</span>
                <span className="text-muted-bone">${totalSupplierCost.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="overline">Gross margin</span>
                <span style={{ color: "var(--rs-terracotta)" }}>${margin.toFixed(2)}</span>
              </div>
            </div>

            <form onSubmit={onSave} className="p-6 space-y-4">
              <label className="block">
                <span className="overline block mb-2">Status — {STATUS_HINT[editor.status]}</span>
                <select
                  data-testid="admin-order-status-select"
                  value={editor.status}
                  onChange={(e) => setEditor({ ...editor, status: e.target.value })}
                  className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                  style={{ borderColor: "var(--rs-border)" }}
                >
                  {STATUSES.filter((s) => s !== "all").map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="overline block mb-2">Supplier order reference</span>
                <input
                  data-testid="admin-order-supplier-ref-input"
                  value={editor.supplier_order_ref}
                  onChange={(e) => setEditor({ ...editor, supplier_order_ref: e.target.value })}
                  placeholder="The order # from supplier site"
                  className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                  style={{ borderColor: "var(--rs-border)" }}
                />
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline block mb-2">Carrier</span>
                  <input
                    data-testid="admin-order-carrier-input"
                    value={editor.carrier || ""}
                    onChange={(e) => setEditor({ ...editor, carrier: e.target.value })}
                    placeholder="USPS, FedEx, DHL…"
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                </label>
                <label className="block">
                  <span className="overline block mb-2">Tracking number</span>
                  <input
                    data-testid="admin-order-tracking-input"
                    value={editor.tracking_number || ""}
                    onChange={(e) => setEditor({ ...editor, tracking_number: e.target.value })}
                    placeholder="1Z…"
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                </label>
              </div>
              <label className="block">
                <span className="overline block mb-2">Internal notes</span>
                <textarea
                  data-testid="admin-order-notes-input"
                  rows={3}
                  value={editor.admin_notes}
                  onChange={(e) => setEditor({ ...editor, admin_notes: e.target.value })}
                  className="w-full px-3 py-3 bg-transparent border text-bone outline-none resize-none"
                  style={{ borderColor: "var(--rs-border)" }}
                />
              </label>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditor(null)}
                  className="px-6 py-3 text-xs uppercase tracking-[0.22em] text-muted-bone border"
                  style={{ borderColor: "var(--rs-border)" }}
                >
                  Cancel
                </button>
                <button
                  data-testid="admin-order-save-btn"
                  type="submit"
                  disabled={saving}
                  className="px-8 py-3 text-xs uppercase tracking-[0.22em] text-bone disabled:opacity-50"
                  style={{ background: "var(--rs-oxblood)" }}
                >
                  {saving ? "Updating…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
